/**
 * Serializer - converts game state to saveable format
 */

import type { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { HealthComponent } from '@/game/components/Health';
import type { UnitComponent } from '@/game/components/Unit';
import type { BuildingComponent } from '@/game/components/Building';
import type { SpawnerComponent } from '@/game/components/Spawner';
import type { CombatComponent } from '@/game/components/Combat';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { TechSystem } from '@/game/tech/TechSystem';
import type { VisionSystem } from '@/game/systems/VisionSystem';
import type { DiplomacySystem } from '@/game/systems/DiplomacySystem';
import { useGameStore } from '@/state/gameStore';
import type {
  SaveData,
  SavedEntity,
  SavedFactionState,
  SavedDiplomacy,
  SavedTechState,
  SavedVisionState,
} from './SaveData';
import { SAVE_VERSION } from './SaveData';
import type { FactionId } from '@/types';

export class Serializer {
  /**
   * Serialize the entire game state
   */
  static serialize(
    world: World,
    factionManager: FactionManager,
    techSystem: TechSystem,
    visionSystem: VisionSystem,
    diplomacySystem: DiplomacySystem,
    name: string = 'Quick Save'
  ): SaveData {
    const state = useGameStore.getState();

    return {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      name,

      config: {
        seed: 0, // TODO: Store actual seed
        difficulty: state.difficulty,
        mapSize: 256,
      },

      gameTime: state.gameTime,
      gameSpeed: state.gameSpeed,
      phase: state.phase,

      playerFaction: state.playerFaction || 'lin',
      playerGold: state.playerGold,

      entities: this.serializeEntities(world),
      factionStates: this.serializeFactionStates(factionManager),
      diplomacy: this.serializeDiplomacy(diplomacySystem),
      techStates: this.serializeTechStates(techSystem),
      visionStates: this.serializeVisionStates(visionSystem),
    };
  }

  private static serializeEntities(world: World): SavedEntity[] {
    const entities: SavedEntity[] = [];

    // Serialize units
    const units = world.query([
      ComponentTypes.Unit,
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Health,
    ]);

    for (const entity of units) {
      const unit = world.getComponent<UnitComponent>(entity, ComponentTypes.Unit)!;
      const pos = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;
      const combat = world.getComponent<CombatComponent>(entity, ComponentTypes.Combat);

      entities.push({
        id: entity,
        type: 'unit',
        factionId: faction.factionId,
        position: { x: pos.x, y: pos.y, z: pos.z },
        health: { current: health.current, max: health.max },
        unitType: unit.unitType,
        targetId: combat?.target ?? null,
      });
    }

    // Serialize buildings
    const buildings = world.query([
      ComponentTypes.Building,
      ComponentTypes.Position,
      ComponentTypes.Faction,
      ComponentTypes.Health,
    ]);

    for (const entity of buildings) {
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;
      const pos = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const health = world.getComponent<HealthComponent>(entity, ComponentTypes.Health)!;
      const spawner = world.getComponent<SpawnerComponent>(entity, ComponentTypes.Spawner);

      const savedEntity: SavedEntity = {
        id: entity,
        type: 'building',
        factionId: faction.factionId,
        position: { x: pos.x, y: pos.y, z: pos.z },
        health: { current: health.current, max: health.max },
        buildingType: building.buildingType,
      };

      if (spawner) {
        savedEntity.spawnerData = {
          unitType: spawner.unitType,
          spawnInterval: spawner.spawnInterval,
          currentCooldown: spawner.spawnTimer,
          rallyX: spawner.rallyX ?? 0,
          rallyZ: spawner.rallyZ ?? 0,
        };
      }

      entities.push(savedEntity);
    }

    return entities;
  }

  private static serializeFactionStates(factionManager: FactionManager): SavedFactionState[] {
    const states: SavedFactionState[] = [];
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun', 'neutral'];

    for (const factionId of factionIds) {
      const state = factionManager.getFactionState(factionId);
      if (state) {
        states.push({
          id: state.id,
          gold: state.gold,
          territoryCount: state.territoryCount,
          unitCount: state.unitCount,
          buildingCount: state.buildingCount,
          isDefeated: state.isDefeated,
          isPlayer: state.isPlayer,
        });
      }
    }

    return states;
  }

  private static serializeDiplomacy(diplomacySystem: DiplomacySystem): SavedDiplomacy {
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];
    const relations: { key: string; value: number }[] = [];

    // Get all relations
    for (let i = 0; i < factionIds.length; i++) {
      for (let j = i + 1; j < factionIds.length; j++) {
        const a = factionIds[i];
        const b = factionIds[j];
        const key = a < b ? `${a}:${b}` : `${b}:${a}`;
        // Access relation through getRelationLevel - we need the raw value
        // This is a simplification; in a full implementation we'd need access to raw values
        relations.push({ key, value: 0 });
      }
    }

    // Get treaties
    const treaties: SavedDiplomacy['treaties'] = [];
    for (const factionId of factionIds) {
      const factionTreaties = diplomacySystem.getTreatiesFor(factionId);
      for (const treaty of factionTreaties) {
        // Avoid duplicates
        if (!treaties.find(t => t.id === treaty.id)) {
          treaties.push({
            id: treaty.id,
            type: treaty.type,
            parties: treaty.parties,
            startTime: treaty.startTime,
            duration: treaty.duration,
          });
        }
      }
    }

    return { relations, treaties };
  }

  private static serializeTechStates(techSystem: TechSystem): SavedTechState[] {
    const states: SavedTechState[] = [];
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];

    for (const factionId of factionIds) {
      const researched = techSystem.getResearchedTechs(factionId);
      const lockedTechs = techSystem.getLockedTechs(factionId);
      const currentResearch = techSystem.getCurrentResearch(factionId);

      states.push({
        factionId,
        researched,
        lockedTechs,
        currentResearch: currentResearch
          ? {
              techId: currentResearch.techId,
              progress: currentResearch.progress,
            }
          : null,
      });
    }

    return states;
  }

  private static serializeVisionStates(visionSystem: VisionSystem): SavedVisionState[] {
    const states: SavedVisionState[] = [];
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];

    for (const factionId of factionIds) {
      const grid = visionSystem.getVisionGrid(factionId);
      if (grid) {
        // Compress grid data (run-length encoding could be used here)
        // For now, just convert to array (simplified)
        const gridData: number[] = [];
        for (let i = 0; i < Math.min(grid.length, 10000); i += 100) {
          gridData.push(grid[i]); // Sample every 100th value for compression
        }
        states.push({ factionId, gridData });
      }
    }

    return states;
  }
}
